import type { EventSubscription, MonitorChangeEvent, MonitorInfo, ViewportChangeEvent, ViewportInfo } from '../types';

export interface ScreenContext {
	monitor: MonitorInfo;
	timestamp: number;
	viewport: ViewportInfo;
}

export interface GlobalScreenChangeEvent {
	data: MonitorChangeEvent | ScreenContext | ViewportChangeEvent;
	source: string;
	timestamp: number;
	type: 'context' | 'monitor' | 'viewport';
}

export type GlobalScreenChangeCallback = (event: GlobalScreenChangeEvent) => void;

export interface ComponentRegistration {
	callback: GlobalScreenChangeCallback;
	id: string;
	interests: Array<'context' | 'monitor' | 'viewport'>;
	name: string;
	registered: number;
}

class GlobalScreenEventBusImpl {
	private components = new Map<string, ComponentRegistration>();
	private eventHistory: GlobalScreenChangeEvent[] = [];
	private readonly maxHistorySize = 50;
	private componentCounter = 0;

	registerComponent(
		name: string,
		callback: GlobalScreenChangeCallback,
		interests: Array<'context' | 'monitor' | 'viewport'> = ['viewport', 'monitor', 'context']
	): EventSubscription {
		const id = this.generateComponentId(name);
		const registration: ComponentRegistration = {
			callback,
			id,
			interests,
			name,
			registered: Date.now(),
		};

		this.components.set(id, registration);

		return {
			callback: callback as never,
			created: registration.registered,
			id,
			options: {} as never,
			unsubscribe: () => this.unregisterComponent(id),
		};
	}

	unregisterComponent(id: string): void {
		const registration = this.components.get(id);
		if (registration) {
			this.components.delete(id);
		}
	}

	emitViewportChange(event: ViewportChangeEvent, source = 'unknown'): void {
		const globalEvent: GlobalScreenChangeEvent = {
			data: event,
			source,
			timestamp: Date.now(),
			type: 'viewport',
		};

		this.broadcastEvent(globalEvent);
	}

	emitMonitorChange(event: MonitorChangeEvent, source = 'unknown'): void {
		const globalEvent: GlobalScreenChangeEvent = {
			data: event,
			source,
			timestamp: Date.now(),
			type: 'monitor',
		};

		this.broadcastEvent(globalEvent);
	}

	emitScreenContextChange(context: ScreenContext, source = 'unknown'): void {
		const globalEvent: GlobalScreenChangeEvent = {
			data: context,
			source,
			timestamp: Date.now(),
			type: 'context',
		};

		this.broadcastEvent(globalEvent);
	}

	private broadcastEvent(event: GlobalScreenChangeEvent): void {
		this.addToHistory(event);

		for (const registration of this.components.values()) {
			if (registration.interests.includes(event.type)) {
				try {
					registration.callback(event);
				} catch (error) {
					console.error(`GlobalScreenEventBus: Fehler in Komponente "${registration.name}":`, error);
				}
			}
		}
	}

	private addToHistory(event: GlobalScreenChangeEvent): void {
		this.eventHistory.push(event);

		if (this.eventHistory.length > this.maxHistorySize) {
			this.eventHistory.shift();
		}
	}

	private generateComponentId(name: string): string {
		return `${name}-${++this.componentCounter}-${Date.now()}`;
	}

	getRegisteredComponents(): ComponentRegistration[] {
		return Array.from(this.components.values());
	}

	getEventHistory(): GlobalScreenChangeEvent[] {
		return [...this.eventHistory];
	}

	destroy(): void {
		this.components.clear();
		this.eventHistory = [];
	}
}

let globalEventBus: GlobalScreenEventBusImpl | null = null;

export function getGlobalScreenEventBus(): GlobalScreenEventBusImpl {
	if (!globalEventBus) {
		globalEventBus = new GlobalScreenEventBusImpl();
	}
	return globalEventBus;
}

export function destroyGlobalScreenEventBus(): void {
	if (globalEventBus) {
		globalEventBus.destroy();
		globalEventBus = null;
	}
}

export function subscribeToScreenChanges(
	componentName: string,
	callback: GlobalScreenChangeCallback,
	interests?: Array<'context' | 'monitor' | 'viewport'>
): EventSubscription {
	return getGlobalScreenEventBus().registerComponent(componentName, callback, interests);
}

export function emitGlobalViewportChange(event: ViewportChangeEvent, source?: string): void {
	getGlobalScreenEventBus().emitViewportChange(event, source);
}

export function emitGlobalMonitorChange(event: MonitorChangeEvent, source?: string): void {
	getGlobalScreenEventBus().emitMonitorChange(event, source);
}

export function emitGlobalScreenContextChange(context: ScreenContext, source?: string): void {
	getGlobalScreenEventBus().emitScreenContextChange(context, source);
}
